import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  LoadingSkeleton,
  Modal,
} from '@nexora/design-system';

/* ── Contratos reais ──
 * GET /logistics/freight-tables  → { data: FreteTabelaDto[], total }
 * POST /logistics/freight-tables → FreteTabelaDto
 * GET /logistics/freight-tables/:id → detalhe (components, auditLog)
 */

interface FreteTabelaDto {
  id: string;
  name: string;
  clientId?: string | null;
  route?: string | null;
  vigenciaInicio?: string | null;
  vigenciaFim?: string | null;
  freightRate?: number | null;
  adValorem?: number | null;
  gris?: number | null;
  pedagio?: number | null;
  status: string;
  createdAt: string;
}

export function FreightTablesPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    route: '',
    freightRate: '',
    adValorem: '',
    gris: '',
    pedagio: '',
    vigenciaInicio: '',
    vigenciaFim: '',
  });

  const listQ = useQuery({
    queryKey: ['logistics', 'freight-tables'],
    queryFn: async () => {
      const data = await apiClient.get<any>('/logistics/freight-tables', { params: { page: '1', limit: '100' } });
      return data as { data: FreteTabelaDto[] };
    },
    enabled: !!tenantId,
  });

  const create = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        name: form.name,
        route: form.route || undefined,
        freightRate: form.freightRate ? Number(form.freightRate) : undefined,
        adValorem: form.adValorem ? Number(form.adValorem) : undefined,
        gris: form.gris ? Number(form.gris) : undefined,
        pedagio: form.pedagio ? Number(form.pedagio) : undefined,
        vigenciaInicio: form.vigenciaInicio ? new Date(form.vigenciaInicio).toISOString() : undefined,
        vigenciaFim: form.vigenciaFim ? new Date(form.vigenciaFim).toISOString() : undefined,
      };
      const res = await apiClient.post('/logistics/freight-tables', payload);
      return res;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['logistics', 'freight-tables'] });
      setModalOpen(false);
      setForm({ name: '', route: '', freightRate: '', adValorem: '', gris: '', pedagio: '', vigenciaInicio: '', vigenciaFim: '' });
    },
  });

  const items = listQ.data?.data ?? [];

  return (
    <Card variant="outlined" padding="0" className="page-card">
      <CardHeader className="px-md pt-md" action={<Button onClick={() => setModalOpen(true)}>Nova Tabela</Button>}>
        Tabela de Fretes — Ad Valorem, GRIS, Pedágio e componentes
      </CardHeader>
      <CardBody>
        {listQ.isLoading ? (
          <LoadingSkeleton />
        ) : items.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--on-surface-variant)' }}>
            Nenhuma tabela de frete cadastrada.
          </div>
        ) : (
          <table className="ds-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Cliente / Contrato</th>
                <th>Rota</th>
                <th>Vigência</th>
                <th style={{ textAlign: 'right' }}>Frete Peso (R$/kg)</th>
                <th style={{ textAlign: 'right' }}>Ad Valorem</th>
                <th style={{ textAlign: 'right' }}>GRIS</th>
                <th style={{ textAlign: 'right' }}>Pedágio</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td>{t.route ?? '—'}</td>
                  <td>
                    {t.vigenciaInicio ? formatDate(t.vigenciaInicio) : '—'}
                    {t.vigenciaFim ? ` → ${formatDate(t.vigenciaFim)}` : ''}
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.freightRate != null ? formatCurrency(t.freightRate) : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{t.adValorem != null ? `${t.adValorem}%` : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{t.gris != null ? `${t.gris}%` : '—'}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.pedagio != null ? formatCurrency(t.pedagio) : '—'}</td>
                  <td><Badge variant={t.status === 'ATIVA' ? 'success' : t.status === 'AGENDADA' ? 'warning' : 'neutral'} size="sm">{t.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Nova Tabela de Frete">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!form.name) return;
            create.mutate();
          }}
          style={{ display: 'grid', gap: '1rem' }}
        >
          <Input label="Nome / Cliente *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <Input label="Rota" value={form.route} onChange={(e) => setForm((f) => ({ ...f, route: e.target.value }))} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Vigência início" type="date" value={form.vigenciaInicio} onChange={(e) => setForm((f) => ({ ...f, vigenciaInicio: e.target.value }))} />
            <Input label="Vigência fim" type="date" value={form.vigenciaFim} onChange={(e) => setForm((f) => ({ ...f, vigenciaFim: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Frete Peso (R$/kg)" type="number" value={form.freightRate} onChange={(e) => setForm((f) => ({ ...f, freightRate: e.target.value }))} />
            <Input label="Ad Valorem (%)" type="number" value={form.adValorem} onChange={(e) => setForm((f) => ({ ...f, adValorem: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="GRIS (%)" type="number" value={form.gris} onChange={(e) => setForm((f) => ({ ...f, gris: e.target.value }))} />
            <Input label="Pedágio (R$)" type="number" value={form.pedagio} onChange={(e) => setForm((f) => ({ ...f, pedagio: e.target.value }))} />
          </div>
          {create.isError && <div role="alert" style={{ color: 'var(--error)' }}>Erro ao salvar tabela.</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" disabled={!form.name || create.isPending}>{create.isPending ? 'Salvando…' : 'Salvar'}</Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}